// 로그인 #/login — 직원 이름 버튼 → PIN 키패드(3×4). 5회 실패(429)면 키패드 잠금 «15분 뒤 다시»
import { useCallback, useEffect, useState } from "react";
import { setSession } from "../auth";
import { api, HttpError } from "../data/api";
import { go } from "../ui/hooks";
import { Icon } from "../ui/icons";
import { Logo } from "../ui/Shell";

type PublicStaff = { id: string; name: string; admin: boolean };
const PIN_MAX = 8;

export function Login() {
  const [staff, setStaff] = useState<PublicStaff[] | null>(null);
  const [loadErr, setLoadErr] = useState(false);
  const [sel, setSel] = useState<PublicStaff | null>(null);
  const [pin, setPin] = useState("");
  const [msg, setMsg] = useState<{ text: string; tone: "error" | "info" } | null>(null);
  const [locked, setLocked] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoadErr(false);
    api<{ staff: PublicStaff[] }>("/api/staff/public")
      .then((r) => setStaff([...r.staff].sort((a, b) => Number(b.admin) - Number(a.admin) || a.name.localeCompare(b.name, "ko"))))
      .catch(() => setLoadErr(true));
  }, []);
  useEffect(load, [load]);

  const choose = (s: PublicStaff) => {
    setSel(s);
    setPin("");
    setMsg(null);
    setLocked(false);
  };

  const submit = useCallback(async () => {
    if (!sel || pin.length < 4 || busy || locked) return;
    setBusy(true);
    try {
      const r = await api<{ token: string; staff: PublicStaff }>("/api/auth/login", { body: { staffId: sel.id, pin } });
      setSession(r.token, r.staff);
      go("");
    } catch (e) {
      setPin("");
      if (e instanceof HttpError && e.status === 429) {
        setLocked(true);
        setMsg({ text: "PIN 을 5번 틀려 잠겼습니다. 15분 뒤 다시 시도하세요.", tone: "error" });
      } else if (e instanceof HttpError && e.status === 401) {
        setMsg({ text: "PIN 이 맞지 않습니다. 5번 틀리면 15분 동안 잠깁니다.", tone: "error" });
      } else {
        setMsg({ text: "서버에 연결하지 못했습니다. 인터넷 연결을 확인하세요.", tone: "error" });
      }
    } finally {
      setBusy(false);
    }
  }, [sel, pin, busy, locked]);

  const press = useCallback((k: string) => {
    if (locked || busy) return;
    setMsg(null);
    if (k === "del") setPin((p) => p.slice(0, -1));
    else if (k === "ok") void submit();
    else setPin((p) => (p.length >= PIN_MAX ? p : p + k));
  }, [locked, busy, submit]);

  // 실물 키보드: 숫자·Backspace·Enter
  useEffect(() => {
    if (!sel) return;
    const on = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") press("del");
      else if (e.key === "Enter") press("ok");
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [sel, press]);

  return (
    <div className="login">
      <div className="login-card">
        <div className="login-intro">
          <Logo full />
          <h1>클리닉 관리</h1>
          <p className="muted">이름을 고르고 PIN 을 입력하세요. 같은 계정으로 웹과 앱이 함께 맞춰집니다.</p>
        </div>
        <div className="login-body">
          <p className="label">직원</p>
          {loadErr ? (
            <div className="banner banner-error">
              <span className="grow">직원 목록을 불러오지 못했습니다.</span>
              <button type="button" className="btn" onClick={load}>다시 시도</button>
            </div>
          ) : !staff ? (
            <p className="muted">불러오는 중…</p>
          ) : (
            <div className="staff-grid" role="radiogroup" aria-label="직원 선택">
              {staff.map((s) => (
                <button key={s.id} type="button" role="radio" aria-checked={sel?.id === s.id}
                  className={"staff-btn" + (sel?.id === s.id ? " on" : "")} onClick={() => choose(s)}>
                  <span className="staff-name">{s.name}</span>
                  {s.admin ? <span className="staff-role">관리자</span> : null}
                </button>
              ))}
            </div>
          )}

          <div className={"pin-area" + (sel ? "" : " pin-idle")}>
            <p className="label">{sel ? `${sel.name} 님 PIN` : "PIN"}</p>
            <div className="pin-dots" aria-label={`PIN ${pin.length}자리 입력됨`}>
              {Array.from({ length: Math.max(6, pin.length) }, (_, i) => <span key={i} className={"pin-dot" + (i < pin.length ? " on" : "")} />)}
            </div>
            {msg ? <p className={"login-msg " + msg.tone} role="alert">{locked ? <Icon name="lock" size={16} /> : null} {msg.text}</p> : null}
            <div className="keypad" aria-label="PIN 키패드">
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "del", "0", "ok"].map((k) => (
                <button key={k} type="button" disabled={!sel || locked || (k === "ok" && pin.length < 4) || busy}
                  className={"key" + (k === "ok" ? " key-ok" : k === "del" ? " key-del" : "")}
                  aria-label={k === "del" ? "지우기" : k === "ok" ? "로그인" : k} onClick={() => press(k)}>
                  {k === "del" ? <Icon name="backspace" /> : k === "ok" ? (busy ? "…" : "확인") : k}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <p className="login-foot muted">시험판 · 가짜 데이터</p>
    </div>
  );
}
